import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Fab,
  Menu,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  ViewModule as GridIcon,
  Description as PageIcon,
  Chat as AIChatIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';

import { useCanvasBySlug } from '../../data/canvas/useCanvases';
import { usePageBySlug, usePages } from '../../data/canvas/usePages';
import {
  useWidgets,
  useCreateWidget,
  useBulkUpdateWidgetPositions,
  useDuplicateWidget,
  useDeleteWidget,
} from '../../data/canvas/useWidgets';
import { WidgetWizard } from './WidgetWizard';
import { WidgetConfigDialog } from './WidgetConfigDialog';
import { CanvasConfigDialog } from './CanvasConfigDialog';
import { PageConfigDialog } from './PageConfigDialog';
import { PageWizard } from './PageWizard';
import {
  TableWidget,
  ChartWidget,
  FieldKVWidget,
  ControlWidget,
  ImageWidget,
  WidgetErrorBoundary,
} from './widgets';
import { DashboardFilterProvider } from '../../contexts/DashboardFilterContext';
import { PageTitle } from '../PageTitle';
import { BrowserTabTitle } from '../BrowserTabTitle';
import { GridLayoutView } from './GridLayoutView';
import { PageLayoutView } from './PageLayoutView';
import { Widget } from '../../types/canvas.types';
import { useAccount } from '../../auth';

export const PageView: React.FC = () => {
  const { accountId: accountIdParam, canvasSlug, pageSlug } = useParams();
  const navigate = useNavigate();

  // Get actual account UUID from context (URL param is account name)
  const { currentAccount } = useAccount();
  const accountId = currentAccount?.id || accountIdParam || '';

  // React Query hooks
  const {
    data: canvas,
    isLoading: canvasLoading,
    error: canvasError,
  } = useCanvasBySlug(accountId!, canvasSlug!);
  const {
    data: page,
    isLoading: pageLoading,
    error: pageError,
  } = usePageBySlug(accountId!, canvas?.id || '', pageSlug!);
  const { data: pages, isLoading: pagesLoading } = usePages(
    accountId!,
    canvas?.id || '',
  );
  const { data: widgets, isLoading: widgetsLoading } = useWidgets(
    accountId!,
    page?.id || '',
  );
  const createWidgetMutation = useCreateWidget(page?.id || '', accountId!);
  const bulkUpdatePositionsMutation = useBulkUpdateWidgetPositions(
    page?.id || '',
    accountId!,
  );
  const duplicateWidgetMutation = useDuplicateWidget(
    page?.id || '',
    accountId!,
  );
  const deleteWidgetMutation = useDeleteWidget(page?.id || '', accountId!);

  // Widget creation modal state
  const [widgetDialogOpen, setWidgetDialogOpen] = useState(false);
  const [widgetConfigOpen, setWidgetConfigOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null);
  const [canvasConfigOpen, setCanvasConfigOpen] = useState(false);
  const [pageConfigOpen, setPageConfigOpen] = useState(false);
  const [pageWizardOpen, setPageWizardOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState<Widget | null>(null);
  const [newWidget, setNewWidget] = useState({
    title: '',
    widget_type: 'table',
    position: {
      x: Math.floor(Math.random() * 8),
      y: Math.floor(Math.random() * 6),
    },
    size: { width: 4, height: 3 },
  });

  // Handle page tab changes
  const handlePageChange = (
    event: React.SyntheticEvent,
    newPageSlug: string,
  ) => {
    navigate(`/${accountId}/canvases/${canvasSlug}/${newPageSlug}`);
  };

  // Handle widget configuration
  const handleWidgetConfig = (widgetId: string) => {
    const widget = widgets?.find((w) => w.id === widgetId);
    if (widget) {
      setSelectedWidget(widget);
      setWidgetConfigOpen(true);
    }
  };

  // Handle widget duplication
  const handleWidgetDuplicate = async (widgetId: string) => {
    try {
      await duplicateWidgetMutation.mutateAsync(widgetId);
    } catch (error) {
      console.error('Error duplicating widget:', error);
    }
  };

  // Render widget based on type
  const renderWidget = (widget: any) => {
    const commonProps = {
      onEdit: handleWidgetConfig,
      onDuplicate: handleWidgetDuplicate,
    };

    const widgetTitle = widget.configuration?.title || widget.widget_type;

    let content;
    switch (widget.widget_type) {
      case 'table':
        content = <TableWidget widget={widget} {...commonProps} />;
        break;
      case 'chart':
        content = <ChartWidget widget={widget} {...commonProps} />;
        break;
      case 'field_kv':
        content = <FieldKVWidget widget={widget} {...commonProps} />;
        break;
      case 'control':
        content = <ControlWidget widget={widget} {...commonProps} />;
        break;
      case 'image':
        content = <ImageWidget widget={widget} {...commonProps} />;
        break;
      default:
        content = (
          <Card>
            <CardContent>
              <Typography variant="h6">
                {widget.title || 'Untitled Widget'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unsupported widget type: {widget.widget_type}
              </Typography>
            </CardContent>
          </Card>
        );
    }

    return (
      <WidgetErrorBoundary widgetTitle={widgetTitle}>
        {content}
      </WidgetErrorBoundary>
    );
  };

  // Handle loading states
  if (canvasLoading || pageLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {canvasLoading ? 'Loading canvas...' : 'Loading page...'}
        </Typography>
      </Box>
    );
  }

  // Handle error states
  if (canvasError || pageError) {
    return (
      <Alert severity="error">
        Error loading {canvasError ? 'canvas' : 'page'}:{' '}
        {canvasError?.message || pageError?.message}
      </Alert>
    );
  }

  // Handle not found states
  if (!canvas || !page) {
    return (
      <Alert severity="warning">
        {!canvas
          ? `Canvas "${canvasSlug}" not found`
          : `Page "${pageSlug}" not found`}
      </Alert>
    );
  }

  return (
    <DashboardFilterProvider>
      <Box sx={{ p: 3 }}>
        {/* Canvas title as main heading with settings button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="h4">{canvas.name}</Typography>
          <Tooltip title="Canvas Settings">
            <IconButton
              onClick={() => setCanvasConfigOpen(true)}
              size="large"
              sx={{ ml: 1 }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Page Navigation Tabs */}
        {pages && pages.length > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tabs
                value={pageSlug}
                onChange={handlePageChange}
                aria-label="page navigation tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flexGrow: 1 }}
              >
                {pages.map((p) => (
                  <Tab
                    key={p.slug}
                    label={p.title}
                    value={p.slug}
                    icon={
                      p.layout_type === 'grid' ? (
                        <GridIcon />
                      ) : p.layout_type === 'page' ? (
                        <PageIcon />
                      ) : (
                        <AIChatIcon />
                      )
                    }
                    iconPosition="start"
                    sx={{ minHeight: 48 }}
                  />
                ))}
              </Tabs>
              <Tooltip title="Add New Page">
                <IconButton
                  onClick={() => setPageWizardOpen(true)}
                  size="small"
                  sx={{
                    ml: 1,
                    color: 'primary.main',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Render layout based on page type */}
        <Box sx={{ mt: 1.5 }}>
          {page.layout_type === 'grid' && (
            <GridLayoutView
              widgets={widgets || []}
              isLoading={widgetsLoading}
              renderWidget={renderWidget}
              onPageSettings={() => setPageConfigOpen(true)}
              onAddWidget={() => setWidgetDialogOpen(true)}
              onEditWidget={(widget) => {
                setSelectedWidget(widget);
                setWidgetConfigOpen(true);
              }}
              onDeleteWidget={(widget) => {
                setWidgetToDelete(widget);
                setDeleteConfirmOpen(true);
              }}
              onLayoutSave={async (updates) => {
                // Transform updates to match the expected format
                const positionUpdates = updates.map((update) => ({
                  widgetId: update.id,
                  position: update.position,
                }));

                // Call the bulk update mutation
                await bulkUpdatePositionsMutation.mutateAsync(positionUpdates);
                console.log('💾 Layout saved successfully!');
              }}
            />
          )}

          {page.layout_type === 'page' && (
            <PageLayoutView
              widgets={widgets || []}
              isLoading={widgetsLoading}
              content={page.settings?.['page_content']}
              renderWidget={renderWidget}
            />
          )}

          {page.layout_type === 'ai_chat' && (
            <AIChatLayoutView
              widgets={widgets || []}
              isLoading={widgetsLoading}
            />
          )}
        </Box>

        {/* Widget Creation Wizard */}
        <WidgetWizard
          open={widgetDialogOpen}
          onClose={() => setWidgetDialogOpen(false)}
          accountId={accountId!}
          pageId={page.id}
          canvas={canvas}
          page={page}
        />

        {/* Widget Configuration Dialog */}
        {selectedWidget && (
          <WidgetConfigDialog
            open={widgetConfigOpen}
            onClose={() => {
              setWidgetConfigOpen(false);
              setSelectedWidget(null);
            }}
            widget={selectedWidget}
            canvas={canvas}
            page={page}
            accountId={accountId!}
            onDelete={() => {
              setWidgetConfigOpen(false);
              setWidgetToDelete(selectedWidget);
              setSelectedWidget(null);
              setDeleteConfirmOpen(true);
            }}
          />
        )}

        {/* Canvas Configuration Dialog */}
        <CanvasConfigDialog
          open={canvasConfigOpen}
          onClose={() => setCanvasConfigOpen(false)}
          canvas={canvas}
          accountId={accountId!}
        />

        {/* Page Configuration Dialog */}
        <PageConfigDialog
          open={pageConfigOpen}
          onClose={() => setPageConfigOpen(false)}
          page={page}
          canvas={canvas}
          accountId={accountId!}
        />

        {/* Page Wizard Dialog */}
        {pageWizardOpen && (
          <Dialog
            open={pageWizardOpen}
            onClose={() => setPageWizardOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <PageWizard />
          </Dialog>
        )}

        {/* Delete Widget Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setWidgetToDelete(null);
          }}
          maxWidth="sm"
        >
          <DialogTitle>Delete Widget?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the widget{' '}
              <strong>
                {widgetToDelete?.configuration?.title || 'Untitled Widget'}
              </strong>
              ? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteConfirmOpen(false);
                setWidgetToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (widgetToDelete) {
                  try {
                    await deleteWidgetMutation.mutateAsync(widgetToDelete.id);
                    setDeleteConfirmOpen(false);
                    setWidgetToDelete(null);
                  } catch (error) {
                    console.error('Failed to delete widget:', error);
                    // Could add error toast here
                  }
                }
              }}
              color="error"
              variant="contained"
              disabled={deleteWidgetMutation.isPending}
            >
              {deleteWidgetMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardFilterProvider>
  );
};

const AIChatLayoutView: React.FC<{ widgets: any[]; isLoading: boolean }> = ({
  widgets,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '2px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        p: 4,
        bgcolor: 'grey.50',
        minHeight: '400px',
      }}
    >
      <AIChatIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h5" color="text.secondary" gutterBottom>
        AI Chat Interface
      </Typography>
      <Typography variant="body1" color="text.secondary">
        AI conversation interface will be displayed here
      </Typography>
    </Box>
  );
};
